import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, Sparkle } from "@phosphor-icons/react";

const PLAN_KEYS = ["family", "legacy", "organization"];

const PLAN_VISUALS = {
  family: { tag: "Most loved", highlight: false },
  legacy: { tag: "Recommended", highlight: true },
  organization: { tag: "For groups", highlight: false },
};

export default function PlanPickerDialog({ open, onOpenChange }) {
  const [step, setStep] = useState("details"); // details | choose | submitting
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [plans, setPlans] = useState([]);
  const [trialDays, setTrialDays] = useState(30);
  const [interval, setInterval] = useState("monthly");
  const [selected, setSelected] = useState("legacy");

  useEffect(() => {
    if (!open) return;
    setStep("details");
    setName("");
    setDescription("");
    setSelected("legacy");
    setInterval("monthly");
    api.get("/billing/plans").then(r => {
      setPlans(r.data.plans);
      setTrialDays(r.data.trial_days);
    }).catch(() => {});
  }, [open]);

  const next = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Give your circle a name first");
      return;
    }
    setStep("choose");
  };

  const submit = async () => {
    setStep("submitting");
    try {
      const { data } = await api.post("/billing/checkout", {
        name: name.trim(),
        description: description.trim(),
        plan: selected,
        interval,
        origin_url: window.location.origin,
      });
      // Redirect to Stripe — full page redirect is the supported pattern.
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't start checkout");
      setStep("choose");
    }
  };

  const planByKey = Object.fromEntries(plans.map(p => [p.key, p]));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (step !== "submitting") onOpenChange(o); }}>
      <DialogContent className="bg-white max-w-3xl p-0 overflow-hidden" data-testid="plan-picker-dialog">
        {step === "details" && (
          <form onSubmit={next} className="p-7 space-y-5">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-forest">Start a new circle</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-sageGray -mt-1">Tell us about the circle, then pick a plan. You'll get {trialDays} days free.</p>
            <div>
              <Label className="text-forest">Circle name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required maxLength={100} className="mt-1.5 bg-white border-sage focus-visible:ring-gold" data-testid="picker-name-input" />
            </div>
            <div>
              <Label className="text-forest">Short description (optional)</Label>
              <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={280} className="mt-1.5 bg-white border-sage focus-visible:ring-gold" data-testid="picker-description-input" />
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-sageGray hover:bg-cream rounded-full">Cancel</Button>
              <Button type="submit" className="bg-forest hover:bg-forestSoft text-ivory rounded-full px-6" data-testid="picker-next">Choose a plan →</Button>
            </div>
          </form>
        )}

        {(step === "choose" || step === "submitting") && (
          <div className="p-7 space-y-5">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-forest">Pick a plan</DialogTitle>
            </DialogHeader>
            <div className="rounded-lg bg-gold/10 border border-gold/40 px-4 py-3 text-sm text-forest flex items-center gap-2" data-testid="picker-trial-banner">
              <Sparkle size={16} weight="fill" className="text-gold" />
              Start your <strong>free {trialDays}-day trial</strong> — no charge today. Cancel anytime before day {trialDays}.
            </div>

            {/* Interval toggle */}
            <div className="flex items-center justify-center gap-1 p-1 bg-cream border border-sage rounded-full w-fit mx-auto" data-testid="picker-interval-toggle">
              <button onClick={() => setInterval("monthly")} className={`text-xs uppercase tracking-widest px-4 py-1.5 rounded-full transition-colors ${interval === "monthly" ? "bg-forest text-ivory" : "text-sageGray hover:text-forest"}`} data-testid="picker-interval-monthly">Monthly</button>
              <button onClick={() => setInterval("yearly")} className={`text-xs uppercase tracking-widest px-4 py-1.5 rounded-full transition-colors ${interval === "yearly" ? "bg-forest text-ivory" : "text-sageGray hover:text-forest"}`} data-testid="picker-interval-yearly">Yearly <span className="text-gold ml-1 normal-case">save ~30%</span></button>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              {PLAN_KEYS.map((key) => {
                const p = planByKey[key];
                if (!p) return null;
                const price = interval === "monthly" ? p.monthly : p.yearly;
                const cadence = interval === "monthly" ? "/ month" : "/ year";
                const isSelected = selected === key;
                const isHighlighted = PLAN_VISUALS[key].highlight;
                return (
                  <button
                    type="button"
                    key={key}
                    onClick={() => setSelected(key)}
                    className={`relative text-left p-5 rounded-2xl border-2 transition-all ${isSelected ? "border-gold bg-gold/5" : "border-sage bg-white hover:border-forest/40"}`}
                    data-testid={`picker-plan-${key}`}
                  >
                    {isHighlighted && (
                      <span className="absolute -top-2.5 right-4 text-[10px] uppercase tracking-widest bg-gold text-forest font-bold px-2 py-0.5 rounded-full">
                        {PLAN_VISUALS[key].tag}
                      </span>
                    )}
                    <p className="text-[11px] uppercase tracking-widest text-gold mb-1">{PLAN_VISUALS[key].tag}</p>
                    <h3 className="font-display text-2xl text-forest leading-tight">{p.name}</h3>
                    <p className="mt-3 text-3xl font-display text-forest">
                      ${price.toFixed(2)}<span className="text-base text-sageGray font-sans"> {cadence}</span>
                    </p>
                    <p className="mt-3 text-sm text-sageGray leading-relaxed">{p.description}</p>
                    {isSelected && (
                      <Badge className="mt-4 bg-forest text-ivory border-0 rounded-full text-[10px] uppercase tracking-widest" data-testid={`picker-selected-${key}`}>
                        <CheckCircle size={11} weight="fill" className="mr-1" /> Selected
                      </Badge>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center justify-between pt-2">
              <Button type="button" variant="ghost" onClick={() => setStep("details")} disabled={step === "submitting"} className="text-sageGray hover:bg-cream rounded-full" data-testid="picker-back">← Back</Button>
              <Button onClick={submit} disabled={step === "submitting"} className="bg-forest hover:bg-forestSoft text-ivory rounded-full px-7" data-testid="picker-checkout">
                {step === "submitting" ? "Redirecting to Stripe…" : `Continue to checkout`}
              </Button>
            </div>
            <p className="text-[11px] text-sageGray text-center">Card required to start trial. We'll charge ${(interval === "monthly" ? planByKey[selected]?.monthly : planByKey[selected]?.yearly)?.toFixed(2) || "—"} on day {trialDays + 1} unless you cancel.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
